import { useState, useEffect, useRef, useCallback } from 'react';
import { Socket, io } from 'socket.io-client';
import styles from './page.module.css';
import Mensagem from '../chat/Mensagem';
import { ChatMensagemDto } from '@tera/shared/src/dto/ChatMensagemDto';
import { useLocalStorage } from '../../lib/useLocalStorage';
import { useSearchParams } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiAccount, mdiCamera, mdiFullscreen, mdiFullscreenExit, mdiRobot, mdiSend } from '@mdi/js';
import Botao from '../../components/Botao';
import Modal from '@/components/layout/Modal';
import Selfie from '@/components/perfil/Selfie';
import { nanoid } from 'nanoid';
import fscreen from 'fscreen';

const App = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useLocalStorage<string | null>(
    'sessionId',
    '',
  );
  const [yourName, setYourName] = useLocalStorage<string | null>(
    'yourName',
    '',
  );

  const [onlineAttendants, setOnlineAttendants] = useState<any[]>([]);
  const [nomeBot, setNomeBot] = useState('Bot');

  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();

  const apikey = searchParams.get('apikey') ?? '';
  const did = searchParams.get('did') ?? '';
  const authParam = searchParams.get('a') ?? '';
  const text = searchParams.get('t') ?? '';

  const [inputName, setInputName] = useState('');

  const socket = useRef<Socket>(
    io('/wswebchat', {
      timeout: 5000,
      autoConnect: false,
      withCredentials: true,
      transports: ['websocket'],
      auth: {
        token: sessionId ?? '',
        apikey,
        did,
        auth: authParam,
        text,
      },
    }),
  );

  const handleSession = useCallback(
    (data: any) => {
      setSessionId(data.sessionId);
    },
    [setSessionId],
  );

  // Nome vindo dos parâmetros iniciais (ex.: transferência de canal): pula o
  // prompt "Qual seu nome?" e libera o input de mensagem direto.
  const handleServerName = useCallback(
    (serverName: string) => {
      if (serverName) setYourName(serverName);
    },
    [setYourName],
  );

  // Nome do bot (config do tenant) para rotular as mensagens do bot corretamente.
  const handleBotName = useCallback((serverBotName: string) => {
    if (serverBotName) setNomeBot(serverBotName);
  }, []);

  const handleMessage = useCallback((message: any) => {
    setMessages(prevMessages => [message, ...prevMessages]);
    // scroll na área de mensagens para a mensagem mais recente
    const messagesContainer = document.getElementById('messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTo({
        top: 0,
        // behavior: 'smooth',
      });
    }
    console.log('new message', message);
  }, []);

  const handleChat = useCallback((chat: any) => {

    console.log('chat.estado', chat.estado, chat.kioskMode);

    if (chat.estado === 'finalizado' && chat.kioskMode) {
      location.reload();
    }
  }, []);

  const handleMessages = useCallback((messages: any) => {
    setMessages(messages);
    console.log('messages', messages);
  }, []);

  const handleError = useCallback((error: any) => {
    switch (error) {
      case 'missing apikey':
        setError(
          'Opa! Lamentamos, mas parece que uma chave de API não foi fornecida.\nPor favor, entre em contato com o suporte.',
        );
        break;
      case 'invalid apikey':
        setError(
          'Opa! Lamentamos, mas parece que a chave de API fornecida é inválida.\nPor favor, entre em contato com o suporte.',
        );
        break;
      case 'unauthorized':
        setError(
          'Opa! Lamentamos, mas você não está autorizado a acessar este chat.\nPor favor, entre em contato com o suporte.',
        );
        break;
      default:
        setError(error);
        break;
    }
  }, []);

  const handleOnline = useCallback((users: any[]) => {
    setOnlineAttendants(users.filter(Boolean));
  }, []);

  const handleOnlineAdd = useCallback((user: any) => {
    setOnlineAttendants(prev => [...prev, user]);
  }, []);

  const handleOnlineDel = useCallback((uid: number) => {
    setOnlineAttendants(prev => prev.filter(u => u?.id !== uid));
  }, []);

  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {

    const handler = () => {
      setIsFullScreen(fscreen.fullscreenElement !== null);
      console.log('fullscreen', fscreen.fullscreenElement);
    };

    if (fscreen.fullscreenEnabled) {
      fscreen.addEventListener('fullscreenchange', handler, false);
    }
    const _socket = socket.current;

    if (yourName) {
      _socket.emit('name', yourName);
    }

    return () => {
      _socket.disconnect();
      if (fscreen.fullscreenEnabled) {
        if (fscreen.fullscreenElement) {
          fscreen.exitFullscreen().catch(() => {});
        }
        fscreen.removeEventListener('fullscreenchange', handler);
      }
    };
  }, []);

  useEffect(() => {
    const _socket = socket.current;
    _socket.on('session', handleSession);
    _socket.on('name', handleServerName);
    _socket.on('bot', handleBotName);
    _socket.on('chat', handleChat);
    _socket.on('message', handleMessage);
    _socket.on('messages', handleMessages);
    _socket.on('error', handleError);

    _socket.on('online', handleOnline);
    _socket.on('online.add', handleOnlineAdd);
    _socket.on('online.del', handleOnlineDel);

    return () => {
      _socket.off('session', handleSession);
      _socket.off('name', handleServerName);
      _socket.off('bot', handleBotName);
      _socket.off('chat', handleChat);
      _socket.off('message', handleMessage);
      _socket.off('messages', handleMessages);
      _socket.off('error', handleError);

      _socket.off('online', handleOnline);
      _socket.off('online.add', handleOnlineAdd);
      _socket.off('online.del', handleOnlineDel);
    };
  }, [
    handleSession,
    handleServerName,
    handleBotName,
    handleMessage,
    handleMessages,
    handleError,
    handleOnline,
    handleOnlineAdd,
    handleOnlineDel,
  ]);

  const handleSendMedia = (file: File) => {
    const message = {
      id: nanoid(),
      attachments: [
        {
          contentId: nanoid(),
          content: file,
          contentType: file.type,
          filename: file.name,
          size: file.size,
        },
      ],
    };
    socket.current.emit('message', message);
    setInput('');
    setSelfie(false);
  };

  const sendMessage = (msg: string) => {
    if (msg.trim()) {
      const message = {
        id: nanoid(),
        text: msg,
      };
      socket.current.emit('message', message);
    }
  };

  const handleSendMessage = useCallback(() => {
    if (input.trim()) {
      sendMessage(input);
      setInput('');
    }
  }, [input, sendMessage]);

  const handleAcceptCookies = () => {
    socket.current.emit('session');
  };

  const handleSendMyName = () => {
    socket.current.emit('name', inputName);
    setYourName(inputName);
  };

  const handleSelectOption = (option: string) => {
    const message = {
      id: nanoid(),
      text: option,
    };
    socket.current.emit('message', message);
  };

  const lastMessage = messages[0];

  const isSendMessageDisabled =
    !input.trim().length ||
    !sessionId ||
    !yourName ||
    !!lastMessage?.opcoes?.length ||
    !!lastMessage?.metadados?.opcoes?.length;

  const isSendMediaDisabled =
    !sessionId ||
    !yourName ||
    !!lastMessage?.opcoes?.length ||
    !!lastMessage?.metadados?.opcoes?.length;

  const options = lastMessage?.metadados?.opcoes ?? lastMessage?.opcoes;

  const [selfie, setSelfie] = useState(false);

  return (
    <div className={styles[`chat-container`]}>
      <Modal
        show={selfie}
        onClose={() => setSelfie(false)}
        onCancel={() => setSelfie(false)}
        title="Enviar foto"
        hideControls
      >
        <Selfie active={selfie} onSelfieTaken={handleSendMedia} />
      </Modal>

      <div className={styles.header}>
        <span className={styles.headerLabel}>Atendentes online</span>
        <div className={styles[`online-attendants-list`]}>
          <div>
            <Icon path={mdiRobot} size={0.9} color="var(--tc-color-primary)" />
          </div>
          {onlineAttendants.filter(it => Boolean(it)).map(attendant => (
            <div key={attendant.id}>
              {attendant.thumb ? (
                <img
                  title={attendant.nome}
                  src={`data:image/png;base64,${attendant.thumb}`}
                />
              ) : (
                <Icon path={mdiAccount} size={0.9} />
              )}
            </div>
          ))}
        </div>
        {fscreen.fullscreenEnabled && (
          <Botao
            variant="none-success"
            icon={isFullScreen ? mdiFullscreenExit : mdiFullscreen}
            title={isFullScreen ? 'Sair da tela cheia' : 'Modo tela cheia'}
            onClick={() =>
              isFullScreen
                ? fscreen.exitFullscreen()
                : fscreen.requestFullscreen(document.documentElement)
            }
          />
        )}
      </div>
      <div className={styles[`messages`]} id="messages-container">
        {messages.map((msg, idx) => (
          <Mensagem
            key={msg.id}
            mensagens={messages}
            index={idx}
            item={msg}
            espelhar
            autor={
              msg.origem === 'usuario'
                ? yourName
                  ? yourName + ' (você)'
                  : 'Você'
                : msg.origem === 'bot'
                  ? nomeBot
                  : undefined
            }
            onOptionChosen={
              lastMessage.id === msg.id ? handleSelectOption : undefined
            }
            onSelectedKbSection={handleSelectOption}
          />
        ))}
      </div>
      {!sessionId && !yourName && !error && (
        <div className={styles.cookiesMessage}>
          <h4>Cookies!</h4>
          <p>
            Nós usamos cookies para que o chat funcione e você tenha uma
            experiência mais personalizada.
          </p>
          <p>Veja mais detalhes em nossa Política de privacidade</p>
          <button className={styles.button} onClick={handleAcceptCookies}>
            Aceitar
          </button>
        </div>
      )}

      {sessionId && !yourName && !error && (
        <div className={styles.cookiesMessage}>
          <h4>Qual seu nome?</h4>
          <p>(Usaremos para deixar a conversa mais personalizada)</p>
          <input
            type="text"
            value={inputName ?? ''}
            onChange={e => setInputName(e.target.value)}
            className={styles.input}
          />
          <button className={styles.button} onClick={handleSendMyName}>
            Enviar
          </button>
        </div>
      )}
      {sessionId && yourName && !error && (
        <div className={styles.footer}>
          <input
            type="text"
            className={styles.input}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
            disabled={!!options?.length}
            placeholder={
              options?.length
                ? 'Escolha uma opção acima'
                : 'Digite sua mensagem...'
            }
          />
          {!isSendMediaDisabled && (
            <Botao
              variant="none-primary"
              icon={mdiCamera}
              onClick={() => setSelfie(true)}
            />
          )}
          <Botao
            disabled={isSendMessageDisabled}
            variant="primary"
            onClick={handleSendMessage}
            icon={mdiSend}
          ></Botao>
        </div>
      )}
      {error && (
        <div className={styles.error}>
          <h4>Erro</h4>
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};

export default App;
